import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Settings, Download, LogOut, Trash2, Globe, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiDownload } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const Row = ({ title, desc, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border py-5 last:border-0">
    <div>
      <div className="font-medium text-navy">{title}</div>
      {desc && <div className="text-sm text-slate-500">{desc}</div>}
    </div>
    {children}
  </div>
);

const Instellingen = () => {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useT();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { api("/digest/preferences").then(setPrefs).catch(() => setPrefs({ frequency: "off", marketing_consent: false })); }, []);

  const setFrequency = async (frequency) => {
    setPrefs((p) => ({ ...p, frequency }));
    try { await api("/digest/preferences", { method: "POST", body: JSON.stringify({ frequency }) }); toast.success(t("p2.set.saved")); }
    catch (e) { toast.error(e.message); }
  };
  const setConsent = async (consent) => {
    setPrefs((p) => ({ ...p, marketing_consent: consent }));
    try { await api("/digest/marketing-consent", { method: "POST", body: JSON.stringify({ consent }) }); }
    catch (e) { toast.error(e.message); }
  };
  const exportData = async () => {
    try { await apiDownload("/me/export", "woningalert-data.json"); toast.success(t("p2.set.exported")); }
    catch (e) { toast.error(e.message); }
  };
  const doLogout = async () => { await logout(); navigate("/"); };
  const deleteAccount = async () => {
    setDeleting(true);
    try { await api("/me", { method: "DELETE" }); await logout(); navigate("/"); toast.success(t("p2.set.deleted")); }
    catch (e) { toast.error(e.message); setDeleting(false); }
  };

  return (
    <div>
      <PageHeader icon={Settings} title={t("p2.set.title")} subtitle={user?.email} />
      <div className="mx-auto max-w-2xl space-y-5 px-4 py-8 sm:px-6">
        <div className="rounded-3xl border border-border bg-white px-6 shadow-sm">
          <Row title={t("p2.set.lang")} desc={t("p2.set.langDesc")}>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger className="w-[140px] rounded-xl" data-testid="settings-lang"><Globe size={14} className="mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nl">Nederlands</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          <Row title={t("p2.set.digest")} desc={t("p2.set.digestDesc")}>
            <Select value={prefs?.frequency || "off"} onValueChange={setFrequency}>
              <SelectTrigger className="w-[140px] rounded-xl" data-testid="settings-digest"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="off">{t("p2.set.off")}</SelectItem>
                <SelectItem value="daily">{t("p2.freq.daily")}</SelectItem>
                <SelectItem value="weekly">{t("p2.freq.weekly")}</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          <Row title={t("p2.set.marketing")} desc={t("p2.set.marketingDesc")}>
            <Switch checked={!!prefs?.marketing_consent} onCheckedChange={setConsent} data-testid="settings-marketing" />
          </Row>
        </div>

        <div className="rounded-3xl border border-border bg-white px-6 shadow-sm">
          <Row title={t("p2.set.data")} desc={t("p2.set.dataDesc")}>
            <Button variant="outline" className="rounded-xl" onClick={exportData} data-testid="settings-export"><Download size={16} className="mr-2" /> {t("p2.set.export")}</Button>
          </Row>
          <Row title={t("nav.logout")}>
            <Button variant="outline" className="rounded-xl" onClick={doLogout} data-testid="settings-logout"><LogOut size={16} className="mr-2" /> {t("nav.logout")}</Button>
          </Row>
        </div>

        <div className="rounded-3xl border border-danger/30 bg-danger/5 px-6 shadow-sm">
          <Row title={t("p2.set.delTitle")} desc={t("p2.set.delDesc")}>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="rounded-xl border-danger/40 text-danger hover:bg-danger/10" data-testid="settings-delete"><Trash2 size={16} className="mr-2" /> {t("p2.set.delBtn")}</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("p2.set.delConfirmTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("p2.set.delConfirmDesc")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteAccount} disabled={deleting} className="bg-danger hover:bg-danger/90" data-testid="settings-delete-confirm">
                    {deleting ? <Loader2 className="animate-spin" size={18} /> : t("p2.set.delYes")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default Instellingen;
